import React, {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import classNames from 'classnames/bind';
import { Autocomplete, InputAdornment } from '@mui/material';
import debounce from 'lodash/debounce';
import styles from './SearchInput.module.scss';
import { SEARCH_ENGINE_OPTIONS } from '@constants';
import { ImageToQueryResponse, Product, SearchService } from '@services';
import { ProductSuggestionItem } from 'components/ProductSuggestionItem';
import {
  Button,
  TextField,
  SearchSendButton,
  VoiceInputButton,
  ImageUploadButton,
  SearchImagePreview,
  ScrollableProductCards,
} from '@components';
import MXPLogo from '@assets/images/partner-logos/mxp.png';
import AlgoliaLogo from '@assets/images/partner-logos/algolia.png';
import Highlighter from 'react-highlight-words';
import { Icon, useMediaQuery, breakpoints, Skeleton, Column, Row } from 'gd-design-library';
import searchStar from '../../assets/images/search-star.svg';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { fileToBase64 } from 'components/SmartSearch/utils';

const CN = 'search-input';
const fullScreenClassName = `${CN}--full-screen`;
const inputBoxClassName = `${CN}--input-box`;
const autocompleteClassName = `${CN}--autocomplete`;
const inputBoxWithSuggestionsClassName = `${CN}--input-box-with-suggestions`;
const autocompletePaperClassName = `${CN}--autocomplete-paper`;
const poweredByClassName = `${CN}--powered-by`;
const startAdornmentClassName = `${CN}--start-adornment`;
const suggestionStarIconClassName = `${CN}--suggestion-star-icon`;
const inputClassName = `${CN}--input`;
const endAdornmentClassName = `${CN}--end-adornment`;
const mobileSuggestionsClassName = `${CN}--mobile-suggestions`;
const suggestionClassName = `${CN}--suggestion`;
const questionSuggestionClassName = `${CN}--question-suggestion`;
const cx = classNames.bind(styles);

enum SuggestionsE {
  QUERY = 'query',
  PRODUCT = 'suggestion'
}

const MIN_SEARCH_TERM_LENGTH = 2;

const MXP_POWERED_BY = {
  logo: MXPLogo,
  url: 'https://cloud.google.com/solutions/retail-product-discovery'
};

const ALGOLIA_POWERED_BY = {
  logo: AlgoliaLogo,
  url: 'https://www.algolia.com/?utm_source=react-instantsearch&utm_medium=website&utm_content=&utm_campaign=poweredby'
};

interface SearchInputProps {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  onSearch: (newSearchTerm?: string) => void;
  searchEngine: SEARCH_ENGINE_OPTIONS;
  onRedirect: (productID: string) => void;
  isConversationalSearchEnabled?: boolean;
}

interface Suggestion {
  key: string;
  group: string;
  type: SuggestionsE;
  data: string | Product;
  isUserQuestion?: boolean;
}

type InputChangeHandler = (e: SyntheticEvent<Element>, value: string) => Promise<void>;

export const SearchInput: React.FC<SearchInputProps> = ({
  searchTerm,
  setSearchTerm,
  onSearch,
  onRedirect,
  searchEngine,
  isConversationalSearchEnabled
}) => {
  const [ suggestions, setSuggestions ] = useState<Suggestion[]>([]);
  const [ conversationStarters, setConversationStarters ] = useState<Suggestion[]>([]);
  const [ areSuggestionsLoading, setAreSuggestionsLoading ] = useState(false);
  const [ hasLoadedConversationStarters, setHasLoadedConversationStarters ] = useState(false);
  const [ isFullScreen, setIsFullScreen ] = useState(false);
  const [ isMounted, setIsMounted ] = useState(false);
  const [ isListening, setIsListening ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ uploadedImage, setUploadedImage ] = useState<File | null>(null);
  const searchService = useMemo(() => new SearchService(), []);
  const isMobileOrTablet = useMediaQuery(`(max-width: ${breakpoints.md})`);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const isSearchPage = router.pathname.startsWith('/search');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMobileOrTablet && isFullScreen && isMounted && inputRef.current) {
      inputRef.current.focus();
    }
  }, [ isMobileOrTablet, isFullScreen, isMounted ]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const fetchSuggestions = useMemo(
    () =>
      debounce(async (term: string): Promise<void> => {
        const trimmedTerm = term.trim();
        if (trimmedTerm.length < MIN_SEARCH_TERM_LENGTH) {
          setSuggestions([]);

          return;
        }

        setAreSuggestionsLoading(true);
        try {
          const {
            querySuggestions,
            productSuggestions,
            userQuestions
          } = await searchService.getSuggestions(trimmedTerm);

          const allowProducts = trimmedTerm.length >= 3;

          setSuggestions([ ...userQuestions.map(uq => ({
            key: `question:${uq.question}`,
            group: '',
            type: SuggestionsE.QUERY,
            data: uq.question,
            isUserQuestion: true
          })),
          ...querySuggestions.map(query => ({
            key: `query:${query}`,
            group: '',
            type: SuggestionsE.QUERY,
            data: query
          })),
          ...(allowProducts
            ? productSuggestions.map(product => ({
              key: `product:${product.id}`,
              group: 'Products',
              type: SuggestionsE.PRODUCT,
              data: product
            }))
            : []) ]);
        } catch {
          setSuggestions([]);
        } finally {
          setAreSuggestionsLoading(false);
        }
      }, 1000),
    [ searchService ]
  );

  const fetchConversationStarters = async (): Promise<void> => {
    if (hasLoadedConversationStarters) {
      setSuggestions(conversationStarters);

      return;
    }

    setAreSuggestionsLoading(true);
    try {
      const examples = await searchService.getConversationStarterExamples();

      const starters = examples.map(example => ({
        key: `starter:${example}`,
        group: '',
        type: SuggestionsE.QUERY,
        data: example,
        isUserQuestion: true
      }));

      setConversationStarters(starters);
      setSuggestions(starters);
      setHasLoadedConversationStarters(true);
    } catch {
      setConversationStarters([]);
      setSuggestions([]);
    } finally {
      setAreSuggestionsLoading(false);
    }
  };

  const handleInputChange: InputChangeHandler = async (event, value) => {
    setSearchTerm(value);
    if (value.trim().length < MIN_SEARCH_TERM_LENGTH) {
      if (hasLoadedConversationStarters) {
        setSuggestions(conversationStarters);
      } else {
        setSuggestions([]);
      }

      return;
    }
  };

  const handleFocus = (): void => {
    if (isMobileOrTablet) {
      setIsFullScreen(true);
    }
    if (searchTerm.trim().length === 0) {
      fetchConversationStarters();
    }
  };

  const handleBackButtonClick = (e): void => {
    e.stopPropagation();
    if (isSearchPage && !isFullScreen) {
      router.back();
    } else {
      inputRef.current?.blur();
      setIsFullScreen(false);
    }
  };

  const handleChange = (value: Suggestion | string | null): void => {
    if (!value) return;
    if (typeof value === 'string') {
      handleSearch();
    } else if (value.type === SuggestionsE.QUERY && typeof value.data === 'string') {
      handleSearch(value.data);
    } else if (value.type === SuggestionsE.PRODUCT) {
      onRedirect((value.data as Product).id);
    }
    setIsFullScreen(false);
  };

  const handleClearSearch = (): void => {
    setSearchTerm('');
    setSuggestions([]);
  };

  const handleTranscript = (text: string): void => {
    setSearchTerm(text);
  };

  const renderPoweredByInputDecorator = (): React.ReactElement | null => {
    const poweredByConfig = {
      [SEARCH_ENGINE_OPTIONS.MXP]: MXP_POWERED_BY,
      [SEARCH_ENGINE_OPTIONS.ALGOLIA]: ALGOLIA_POWERED_BY,
      [SEARCH_ENGINE_OPTIONS.GRS]: MXP_POWERED_BY,
      [SEARCH_ENGINE_OPTIONS.VAIS]: MXP_POWERED_BY
    };
    const config = poweredByConfig[searchEngine];
    if (!config) return null;

    return (
      <a href={ config.url } target='_blank' rel='noreferrer' className={ cx(poweredByClassName) }>
        <Image src={ config.logo.src } alt='Powered by logo' width={ 60 } height={ 19 } />
      </a>
    );
  };

  const renderSuggestion = (props, option): React.ReactElement => (
    <li { ...props } key={ option.key }>
      {option.type === SuggestionsE.QUERY ? (
        <div className={ cx(suggestionClassName, { [questionSuggestionClassName]: option.isUserQuestion }) }>
          {option.isUserQuestion && (
            <span className={ cx(suggestionStarIconClassName) } aria-hidden='true' />
          )}
          <Highlighter
            searchWords={ searchTerm.split(/\s+/).filter(word => word.length > 0) }
            autoEscape={ true }
            textToHighlight={ option.data as string }
            highlightTag='span'
            highlightStyle={ { fontWeight: 'bold' } }
            style={ { flex: 1 } }
          />
        </div>
      ) : (
        <ProductSuggestionItem product={ option.data as Product } searchTerm={ searchTerm } />
      )}
    </li>
  );

  const productSuggestions = useMemo(
    () => suggestions.filter(suggestion => suggestion.type === SuggestionsE.PRODUCT),
    [ suggestions ]
  );

  const redirectToSearchPageWithImage = async (): Promise<void> => {
    const userMessage = searchTerm.trim();
    let imageToQueryResult: ImageToQueryResponse | undefined;

    if (uploadedImage) {
      try {
        setIsLoading(true);
        const image = uploadedImage;
        imageToQueryResult = await searchService.imageToQuery(image, userMessage);
        const base64Image = await fileToBase64(image);
        sessionStorage.setItem('smartSearchUploadedImage', base64Image);
        sessionStorage.setItem('smartSearchImageToQueryResult', imageToQueryResult.imageToQuery);
        sessionStorage.setItem('smartSearchUserMessage', userMessage);
        setUploadedImage(null);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error converting image to base64:', error);
      } finally {
        setIsLoading(false);
      }
    }

    const encodedQuery = encodeURIComponent(imageToQueryResult?.imageToQuery || userMessage);
    await router.push(`/search/${encodedQuery}?conversational-search=true`);
  };

  const handleSearch = (newSearchTerm: string = searchTerm): void => {
    if (uploadedImage) {
      redirectToSearchPageWithImage();
    } else {
      onSearch(newSearchTerm.trim());
    }
  };

  useEffect(() => {
    if (isMounted && searchTerm.trim().length >= MIN_SEARCH_TERM_LENGTH) {
      setAreSuggestionsLoading(true);
      fetchSuggestions(searchTerm);
    }
  }, [ isMounted, searchTerm, fetchSuggestions ]);

  useEffect(() => (): void => { fetchSuggestions.cancel(); }, [ fetchSuggestions ]);

  useEffect(() => {
    setIsFullScreen(false);
  }, [ router.pathname ]);

  if (isMobileOrTablet && isSearchPage) {
    return null;
  }

  return (
    <div className={ cx(CN, { [fullScreenClassName]: isFullScreen }) }>
      {isMobileOrTablet && isFullScreen && areSuggestionsLoading && (
        <div className={ cx(mobileSuggestionsClassName) } style={ { gap: '1rem' } }>
          <Skeleton width='152px' height='24px' />
          <Row style={ { gap: '0.5rem' } }>
            <Skeleton width='85px' height='85px' />
            <Column style={ { gap: '0.5rem' } }>
              <Skeleton width='187px' height='24px' />
              <Skeleton width='134px' height='24px' />
            </Column>
          </Row>
          <Row style={ { gap: '0.5rem' } }>
            <Skeleton width='85px' height='85px' />
            <Column style={ { gap: '0.5rem' } }>
              <Skeleton width='187px' height='24px' />
              <Skeleton width='134px' height='24px' />
            </Column>
          </Row>
        </div>
      )}
      {isMobileOrTablet && isFullScreen && !areSuggestionsLoading && (
        <ul className={ cx(mobileSuggestionsClassName) }>
          {suggestions.filter(suggestion => suggestion.type !== SuggestionsE.PRODUCT)
            .map(suggestion => renderSuggestion({ onClick: () => handleChange(suggestion) }, suggestion))}
          {productSuggestions.length > 0 && (
            <>
              <li><hr style={ { margin: '0.5rem 0 0 0' } } /></li>
              <li>
                <ScrollableProductCards
                  products={ {
                    results: productSuggestions.map(suggestion => suggestion.data as Product),
                    categoryName: searchTerm,
                  } }
                  title={ `Most Popular ${searchTerm} Products` }
                  analyticsContext={ {
                    listId: `product-suggestions-${searchTerm}`,
                    listName: `Product Suggestions - ${searchTerm}`
                  } }
                />
              </li>
            </>
          )}
        </ul>
      )}
      <div
        className={
          cx(inputBoxClassName, { [inputBoxWithSuggestionsClassName]: suggestions.length > 0 || areSuggestionsLoading })
        }
      >
        <SearchImagePreview
          uploadedImage={ uploadedImage }
          setUploadedImage={ setUploadedImage }
        />
        <Autocomplete
          className={ cx(autocompleteClassName) }
          slotProps={ { paper: { className: cx(autocompletePaperClassName) } } }
          freeSolo
          blurOnSelect
          disableClearable
          includeInputInList
          loading={ !isMobileOrTablet ? areSuggestionsLoading : false }
          options={ !isMobileOrTablet ? suggestions : [] }
          disabled={ isLoading }
          inputValue={ searchTerm }
          onInputChange={ handleInputChange }
          groupBy={ (option): string => option.group }
          onChange={ (e, value): void => handleChange(value as Suggestion) }
          getOptionLabel={ (option): string =>
            typeof option === 'string'
              ? option
              : option.type === SuggestionsE.QUERY
                ? (option.data as string)
                : ((option.data as Product)?.name || '')
          }
          filterOptions={ (o): Suggestion[] => o }
          renderOption={ renderSuggestion }
          renderInput={ (params): React.ReactElement => (
            <TextField
              { ...params }
              placeholder={ isConversationalSearchEnabled ? 'Search or ask anything' : 'Search...' }
              inputRef={ inputRef }
              InputProps={ {
                ...params.InputProps,
                classes: { root: cx(inputClassName) },
                onKeyDown: handleKeyDown,
                onFocus: handleFocus,
                startAdornment: (
                  <InputAdornment className={ cx(startAdornmentClassName) } position='start'>
                    {isMobileOrTablet && (isFullScreen || isSearchPage) ? (
                      <Button
                        variant='inherit'
                        isIcon
                        onClick={ handleBackButtonClick }
                        aria-label='Close search'
                      >
                        <Icon
                          name='arrowForward'
                          width={ 16 }
                          height={ 16 }
                          style={ { transform: 'rotate(180deg)' } }
                        />
                      </Button>
                    ) : (
                      isConversationalSearchEnabled ? (
                        <Image src={ searchStar.src } alt='Search Star' width={ 22 } height={ 22 } />
                      ) : (
                        <Icon name='search' width={ 22 } height={ 22 } />
                      )
                    )}
                  </InputAdornment>
                ),
                endAdornment: (
                  <div className={ cx(endAdornmentClassName) }>
                    {(searchTerm.trim().length === 0 || isListening) && (
                      <VoiceInputButton
                        onTranscript={ handleTranscript }
                        isListening={ isListening }
                        onListeningChange={ setIsListening }
                      />
                    )}
                    {(isConversationalSearchEnabled && isMobileOrTablet) && (
                      <ImageUploadButton
                        setUploadedImage={ setUploadedImage }
                        disabled={ isLoading }
                      />
                    )}
                    {searchTerm.trim().length > 0 && isMobileOrTablet && (
                      <SearchSendButton
                        onClick={ () => handleSearch() }
                        disabled={ isLoading }
                      />
                    )}
                    {(isSearchPage && searchTerm.trim().length > 0) && (
                      <Button
                        variant='inherit'
                        isIcon
                        onClick={ handleClearSearch }
                        aria-label='Clear search'
                      >
                        <Icon name='cross' width={ 14 } height={ 14 } />
                      </Button>
                    )}
                    {renderPoweredByInputDecorator()}
                  </div>
                )
              } }
            />
          ) }
        />
      </div>
    </div>
  );
};
